import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    LinkedContentObjectsContract,
    RideEventContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ContentCollection } from 'src/app/models/content-collection';
import { RideEvent } from 'src/app/models/ride-event';

@Injectable({ providedIn: 'root' })
export class RideEventResource {
    private baseUrl: string;
    private rideEventCache: Record<string, RideEventContract | undefined> = {};
    private rideEventContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};
    private collectionContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'events'
        );
    }

    getRideEvents(): Observable<RideEvent[]> {
        return this.http
            .get<RideEventContract[]>(`${this.baseUrl}?includeDeleted=false`)
            .pipe(
                map((rideEvents) =>
                    rideEvents.map((contract) => new RideEvent(contract))
                )
            );
    }

    getRideEventById(rideEventId: string): Observable<RideEvent> {
        const cachedContract = this.rideEventCache[rideEventId];
        if (cachedContract !== undefined) {
            return of(new RideEvent(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            rideEventId
        );
        return this.http.get<RideEventContract>(url).pipe(
            tap(
                (contract) =>
                    (this.rideEventCache[rideEventId] = cloneDeep(contract))
            ),
            map((contract) => new RideEvent(contract))
        );
    }

    getRideEventContents(rideEventId: string): Observable<ContentCollection> {
        const cachedContract = this.rideEventContentCache[rideEventId];
        if (cachedContract !== undefined) {
            return of(new ContentCollection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            rideEventId,
            'contents'
        );
        return this.http.get<LinkedContentObjectsContract>(url).pipe(
            tap(
                (contract) =>
                    (this.rideEventContentCache[rideEventId] =
                        cloneDeep(contract))
            ),
            map(
                (collection) =>
                    new ContentCollection(collection, (collectionId) => {
                        return this.getRideEventCollectionContents(
                            rideEventId,
                            collectionId
                        );
                    })
            )
        );
    }

    getRideEventCollectionContents(
        rideEventId: string,
        collectionId: string
    ): Observable<ContentCollection> {
        const key = `${rideEventId}${collectionId}`;
        const cachedContract = this.collectionContentCache[key];
        if (cachedContract !== undefined) {
            return of(new ContentCollection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            rideEventId,
            'collections',
            collectionId,
            'contents'
        );
        return this.http.get<LinkedContentObjectsContract>(url).pipe(
            tap(
                (contract) =>
                    (this.collectionContentCache[key] = cloneDeep(contract))
            ),
            map((collection) => new ContentCollection(collection))
        );
    }

    create(rideEvent: RideEvent): Observable<RideEvent> {
        return this.http
            .post<{ id: string }>(this.baseUrl, rideEvent.toContract())
            .pipe(
                map((response) => {
                    const resultRideEvent = rideEvent.clone();
                    resultRideEvent.id = response.id;
                    return resultRideEvent;
                })
            );
    }

    update(rideEvent: RideEvent): Observable<RideEvent> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            rideEvent.id
        );
        return this.http.put<null>(url, rideEvent.toContract()).pipe(
            map(() => {
                this.removeCaches(rideEvent.id);
                return rideEvent;
            })
        );
    }

    delete(rideEventId: string): Observable<null> {
        this.removeCaches(rideEventId);
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            rideEventId
        );
        return this.http.delete<null>(url);
    }

    private removeCaches(rideEventId: string): void {
        this.rideEventCache[rideEventId] = undefined;
        const cacheIdRegex = new RegExp(`^${rideEventId}.*`);

        Object.keys(this.rideEventContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.rideEventContentCache[key] = undefined;
            }
        });

        Object.keys(this.collectionContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.collectionContentCache[key] = undefined;
            }
        });
    }
}
